import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useUser } from "@/auth/UserContext";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useViewerBookingScope } from "@/hooks/useViewerBookingScope";
import { viewerScopeKey } from "@/lib/viewerScopeFilters";
import { api } from "@/services/api";
import type { ApiBooking, RateCardSummary } from "@/types/api";

import {
  filterSearchPages,
  SEARCH_PAGES,
  type SearchPage,
} from "./pageRegistry";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const MAX_BOOKING_RESULTS = 5;
const MAX_RATE_CARD_RESULTS = 4;

export interface GlobalSearchResults {
  pages: SearchPage[];
  bookings: ApiBooking[];
  totalBookings: number;
  rateCards: RateCardSummary[];
}

export interface GlobalSearchState {
  query: string;
  setQuery: (value: string) => void;
  results: GlobalSearchResults;
  loading: boolean;
  reset: () => void;
  prime: () => void;
}

const EMPTY_RESULTS: GlobalSearchResults = {
  pages: [],
  bookings: [],
  totalBookings: 0,
  rateCards: [],
};

export function useGlobalSearch(open: boolean): GlobalSearchState {
  const { isAdmin, canCreateRates, isSalesPerson, permissions } = useUser();
  const { flags } = useFeatureFlags();
  const scopeKey = viewerScopeKey(useViewerBookingScope());

  const browsablePages = useMemo(
    () =>
      SEARCH_PAGES.filter((page) => {
        if (page.adminOnly && !isAdmin) return false;
        if (page.requiresCreateRates && !canCreateRates) return false;
        if (page.hideForSales && isSalesPerson) return false;
        if (page.requiresPermission && !permissions.includes(page.requiresPermission)) {
          return false;
        }
        if (page.featureFlag && !(flags[page.featureFlag] ?? false)) {
          return false;
        }
        return true;
      }),
    [isAdmin, canCreateRates, isSalesPerson, permissions, flags],
  );

  const [query, setQueryRaw] = useState("");
  const [results, setResults] = useState<GlobalSearchResults>(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);

  const requestIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const setQuery = useCallback((value: string) => {
    setQueryRaw(value);
  }, []);

  const reset = useCallback(() => {
    setQueryRaw("");
    setResults(EMPTY_RESULTS);
    setLoading(false);
    requestIdRef.current += 1;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const prime = useCallback(() => {
    setResults({ ...EMPTY_RESULTS, pages: browsablePages });
  }, [browsablePages]);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    prime();
  }, [open, prime, reset]);

  useEffect(() => {
    if (!open) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setResults({ ...EMPTY_RESULTS, pages: browsablePages });
      setLoading(false);
      return;
    }

    const pageResults = filterSearchPages(trimmed, browsablePages);
    setResults((prev) => ({ ...prev, pages: pageResults }));
    setLoading(true);

    timerRef.current = setTimeout(() => {
      if (abortRef.current) {
        abortRef.current.abort();
      }

      const thisRequestId = ++requestIdRef.current;
      const controller = new AbortController();
      abortRef.current = controller;

      const bookingsPromise = api
        .getBookings({ search: trimmed, per_page: MAX_BOOKING_RESULTS })
        .then((resp) => ({
          bookings: resp.data,
          totalBookings: resp.pagination.total,
        }))
        .catch(() => ({ bookings: [] as ApiBooking[], totalBookings: 0 }));

      const rateCardsByCarrierPromise = api
        .getRateCards({ carrier: trimmed, per_page: MAX_RATE_CARD_RESULTS })
        .then((resp) => resp.data)
        .catch(() => [] as RateCardSummary[]);

      const rateCardsByContractPromise = api
        .getRateCards({ contract_ref: trimmed, per_page: MAX_RATE_CARD_RESULTS })
        .then((resp) => resp.data)
        .catch(() => [] as RateCardSummary[]);

      Promise.all([
        bookingsPromise,
        rateCardsByCarrierPromise,
        rateCardsByContractPromise,
      ]).then(([bookingResult, rateCardsByCarrier, rateCardsByContract]) => {
        if (thisRequestId !== requestIdRef.current) return;
        if (controller.signal.aborted) return;

        const rateCardMap = new Map<string, RateCardSummary>();
        for (const card of rateCardsByCarrier) rateCardMap.set(card.id, card);
        for (const card of rateCardsByContract) rateCardMap.set(card.id, card);

        setResults({
          pages: filterSearchPages(trimmed, browsablePages),
          bookings: bookingResult.bookings,
          totalBookings: bookingResult.totalBookings,
          rateCards: [...rateCardMap.values()].slice(0, MAX_RATE_CARD_RESULTS),
        });
        setLoading(false);
      });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [query, open, browsablePages, scopeKey]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  return { query, setQuery, results, loading, reset, prime };
}
